import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Info, ArrowRight, AlertCircle, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import Layout from "@/components/Layout";
import ScoreGauge from "@/components/ScoreGauge";
import PillarBar from "@/components/PillarBar";
import JurisdictionGrid from "@/components/JurisdictionGrid";
import WhyTrustPanel from "@/components/WhyTrustPanel";
import Disclaimer from "@/components/Disclaimer";
import { api } from "@/lib/api";
import ScoreExplanationModal from "@/components/ScoreExplanationModal";
import { Button } from "@/components/ui/button";

// Hardcoded data freshness (static — not bound to any DB)
const DATA_FRESHNESS = {
  lastVerified: "2026-08-18",
  nextReview: "2026-08-25",
};

const SCENARIOS = {
  growing: {
    label: "Growing Store — Score 67/C",
    store_name: "Maple & Co. Home Goods",
    store_url: "demo-store.myshopify.com",
    revenue_tier: "$500k-$1M",
    composite_score: 67,
    grade: "C",
    pillar1: 58,
    pillar2: 65,
    pillar3: 82,
    pillar4: 75,
    header: null,
    jurisdictionAnalysis: {
      FL: {
        status: "compliant",
        summary: "FL sales tax nexus is triggered. Maple & Co. is registered with FL DOR. FDBR privacy law does not apply (revenue below $1B threshold).",
        verifiedSourceProof: {
          authorityName: "Florida Department of Revenue",
          officialURL: "https://floridarevenue.com",
          statuteCitation: "Fla. Stat. § 212.0596",
          lastVerifiedDate: DATA_FRESHNESS.lastVerified,
          nextReviewDate: DATA_FRESHNESS.nextReview,
          storeMatchExplanation: "Revenue tier $500k-$2M exceeds Florida's $100K revenue threshold (effective July 1, 2021). Merchant indicates active registration with FL DOR.",
        },
      },
      TX: {
        status: "action_required",
        summary: "TX sales tax nexus triggered ($500k-$2M revenue exceeds $500K threshold) but merchant is NOT registered with the Texas Comptroller. This is a Tier-1 enforcement state.",
        verifiedSourceProof: {
          authorityName: "Texas Comptroller of Public Accounts",
          officialURL: "https://comptroller.texas.gov",
          statuteCitation: "Tex. Tax Code § 151.107",
          lastVerifiedDate: DATA_FRESHNESS.lastVerified,
          nextReviewDate: DATA_FRESHNESS.nextReview,
          storeMatchExplanation: "Revenue $500K-$1M meets the $500,000 threshold. Texas Comptroller actively pursues unregistered remote sellers. Register immediately to avoid back-tax liability and penalties.",
        },
      },
      NY: {
        status: "not_applicable",
        summary: "Revenue threshold met ($540K above $500K). Transaction count (87) below 100. NY nexus is NOT triggered under the dual-threshold rule.",
        verifiedSourceProof: {
          authorityName: "New York Department of Taxation and Finance",
          officialURL: "https://www.tax.ny.gov",
          statuteCitation: "N.Y. Tax Law § 1101(b)(8)(iv)",
          lastVerifiedDate: DATA_FRESHNESS.lastVerified,
          nextReviewDate: DATA_FRESHNESS.nextReview,
          storeMatchExplanation: "NY requires BOTH $500K revenue AND 100+ transactions simultaneously. Merchant's 87 transactions fall short. Monitor monthly — crossing 100 transactions will trigger nexus.",
        },
      },
      DC: {
        status: "compliant",
        summary: "DC sales tax nexus is triggered. Merchant is registered with DC OTR. No DC consumer privacy law currently enacted.",
        verifiedSourceProof: {
          authorityName: "DC Office of Tax and Revenue",
          officialURL: "https://otr.cfo.dc.gov",
          statuteCitation: "D.C. Code § 47-2201",
          lastVerifiedDate: DATA_FRESHNESS.lastVerified,
          nextReviewDate: DATA_FRESHNESS.nextReview,
          storeMatchExplanation: "Revenue tier exceeds DC's $100K revenue threshold. Merchant indicates active registration with DC OTR.",
        },
      },
    },
    remediationItems: [
      { priority: "CRITICAL", jurisdiction: "TX", action: "Register for sales tax collection in Texas", potentialGain: 22, statuteCitation: "Tex. Tax Code § 151.107", officialURL: "https://comptroller.texas.gov" },
      { priority: "HIGH", jurisdiction: "TX", action: "Add opt-out mechanism for Texas TDPSA compliance", potentialGain: 12, statuteCitation: "Tex. Bus. & Com. Code § 541", officialURL: "https://www.texasattorneygeneral.gov" },
      { priority: "MEDIUM", jurisdiction: "ALL", action: "Add cookie consent banner", potentialGain: 8, statuteCitation: "Best practice for TX and FL", officialURL: "" },
    ],
  },
  established: {
    label: "Established Store — Score 91/A",
    store_name: "Harborview Home Goods",
    store_url: "harborview-demo.myshopify.com",
    revenue_tier: "$1M+",
    composite_score: 91,
    grade: "A",
    pillar1: 100,
    pillar2: 88,
    pillar3: 100,
    pillar4: 100,
    header: "This store has strong readiness. Even high-scoring stores benefit from ongoing monitoring as thresholds drift and new state laws take effect.",
    jurisdictionAnalysis: {
      CA: { status: "compliant", summary: "CA nexus registered. CCPA policies in place." },
      TX: { status: "compliant", summary: "TX nexus registered. TDPSA opt-out present." },
      NY: { status: "compliant", summary: "Both thresholds met. Registered with NY Tax & Finance." },
      FL: { status: "compliant", summary: "FL nexus registered. FDBR exempt — revenue below $1B." },
      CO: { status: "review_suggested", summary: "Review suggested." },
      WA: { status: "review_suggested", summary: "Review suggested." },
    },
    remediationItems: [
      { priority: "LOW", jurisdiction: "Colorado", action: "Consider honoring the Global Privacy Control (GPC) browser signal for Colorado CPA compliance.", potentialGain: 5, statuteCitation: "C.R.S. § 6-1-1308", officialURL: "https://coag.gov" },
      { priority: "LOW", jurisdiction: "Washington", action: "Review whether any health-adjacent products trigger MHMDA scope.", potentialGain: 4, statuteCitation: "RCW § 70.372", officialURL: "https://www.atg.wa.gov" },
    ],
  },
};

const PRIORITY_META = {
  CRITICAL: { color: "var(--rg-red)", Icon: AlertCircle },
  HIGH: { color: "var(--rg-amber)", Icon: AlertTriangle },
  MEDIUM: { color: "var(--rg-sage)", Icon: Info },
  LOW: { color: "var(--rg-muted)", Icon: Info },
};

const STATUS_BADGE = {
  compliant: { label: "COMPLIANT", color: "var(--rg-sage)", Icon: CheckCircle2 },
  review_suggested: { label: "REVIEW SUGGESTED", color: "var(--rg-amber)", Icon: AlertTriangle },
  action_required: { label: "ACTION REQUIRED", color: "var(--rg-red)", Icon: AlertCircle },
  not_applicable: { label: "N/A", color: "var(--rg-muted)", Icon: Info },
};

export default function DemoSandbox() {
  const [scenarioKey, setScenarioKey] = useState("growing");
  // 2026-08-18 — same live-fetch pattern as SampleReport. Keeps the Demo
  // Sandbox "regulatory data last verified" footer in sync with the live
  // DB instead of drifting behind hardcoded DATA_FRESHNESS values.
  const [liveVerification, setLiveVerification] = useState(null);
  useEffect(() => {
    api.get("/public/verification-status")
      .then(({ data }) => {
        if (data && data.last_verified_date) setLiveVerification(data);
      })
      .catch(() => { /* silent — hardcoded fallback covers it */ });
  }, []);
  const D = SCENARIOS[scenarioKey];

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 pt-10 pb-20">
        {/* Scenario tab switcher */}
        <div className="flex gap-2 mb-4 flex-wrap" data-testid="demo-scenario-tabs">
          {Object.entries(SCENARIOS).map(([key, s]) => (
            <Button
              key={key}
              size="sm"
              onClick={() => setScenarioKey(key)}
              className={
                scenarioKey === key
                  ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold"
                  : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)]"
              }
              data-testid={`demo-tab-${key}`}
            >
              {s.label}
            </Button>
          ))}
        </div>

        {/* Demo alert banner — both tabs */}
        <div
          className="rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-4 mb-4 text-sm text-[var(--rg-amber)]"
          data-testid="demo-illustration-banner"
        >
          This is a sample report for a fictional store. All scores and findings are illustrative only.
        </div>

        {/* Per-scenario header (Established only) */}
        {D.header && (
          <div className="rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-4 mb-4 text-sm text-[var(--rg-text)]" data-testid="demo-scenario-header">
            <CheckCircle2 className="w-4 h-4 inline mr-2 text-[var(--rg-sage)]" />
            {D.header}
          </div>
        )}

        {/* CTA banner above the report */}
        <div className="rounded-md border-l-4 border-l-[var(--rg-sage)] border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4 flex flex-col sm:flex-row sm:items-start gap-3 mb-2" data-testid="demo-banner">
          <Info className="w-5 h-5 text-[var(--rg-sage)] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[var(--rg-text-strong)]">This is a demo report for a sample store.</div>
            <div className="text-sm text-[var(--rg-text)]">Your real audit may look different. Audit your own store to see your actual readiness score.</div>
          </div>
          <Link to="/register" data-testid="demo-banner-cta" className="w-full sm:w-auto">
            <Button className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold w-full sm:w-auto">
              Audit My Real Store <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>

        {/* Store header */}
        <div className="mb-8 mt-6">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)]">Readiness Report · Sandbox</div>
          <h1 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-[var(--rg-text-strong)] mt-2">{D.store_name}</h1>
          <div className="text-sm text-[var(--rg-muted)] font-mono mt-1">{D.store_url} · Revenue tier {D.revenue_tier}</div>
        </div>

        {/* Score + pillars grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 flex flex-col items-center justify-center"
            data-testid="demo-score-card"
          >
            <ScoreGauge score={D.composite_score} testId="demo-composite-gauge" />
            <div className="mt-2 text-xs uppercase tracking-[0.18em] font-bold text-[var(--rg-text)]" data-testid="demo-composite-grade">
              Grade {D.grade}
            </div>
            <WhyTrustPanel
              testId="demo-composite-why"
              formulaExplanation="Your score combines four areas: Sales Tax Nexus (35%), Privacy Law Readiness (30%), Policy Documents (20%), and Jurisdiction Awareness (15%). Each area is scored against verified government sources. This demo shows a sample calculation."
            />
            <ScoreExplanationModal />
          </motion.div>

          <div className="lg:col-span-2 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 space-y-6">
            <PillarBar label="Sales Tax Nexus" score={D.pillar1} weight="35%" testId="demo-pillar-1" action={<WhyTrustPanel testId="demo-pillar-1-why" formulaExplanation="Triggered nexus weight = sum of state weights where revenue exceeds threshold. Registered weight = the subset you've registered for." />} />
            <PillarBar label="Privacy Law Readiness" score={D.pillar2} weight="30%" testId="demo-pillar-2" action={<WhyTrustPanel testId="demo-pillar-2-why" formulaExplanation="4 rights per applicable state × 25 pts each." />} />
            <PillarBar label="Policy Documents" score={D.pillar3} weight="20%" testId="demo-pillar-3" action={<WhyTrustPanel testId="demo-pillar-3-why" formulaExplanation="Privacy Policy (22), data-sharing (18), ToS (15), Cookie (13), Refund (12), Privacy contact (12), Retention (8). Max 100." />} />
            <PillarBar label="Exposure Awareness" score={D.pillar4} weight="15%" testId="demo-pillar-4" action={<WhyTrustPanel testId="demo-pillar-4-why" formulaExplanation="Jurisdictions identified (40), nexus documented (30), active monitoring (30)." />} />
          </div>
        </div>

        {/* Jurisdiction grid — Established uses inline list (no source proofs) */}
        <div className="mt-10">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Jurisdiction Analysis</div>
          <h2 className="font-heading font-bold tracking-tight text-2xl sm:text-3xl text-[var(--rg-text-strong)] mb-6">Per-state findings</h2>
          {scenarioKey === "growing" ? (
            <JurisdictionGrid analysis={D.jurisdictionAnalysis} testIdPrefix="demo-jurisdiction" />
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="established-jurisdiction-grid">
              {Object.entries(D.jurisdictionAnalysis).map(([abbr, info]) => {
                const meta = STATUS_BADGE[info.status] || STATUS_BADGE.not_applicable;
                return (
                  <div key={abbr} className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4" data-testid={`established-juris-${abbr}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-heading font-extrabold text-[var(--rg-text-strong)] text-2xl">{abbr}</span>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: meta.color, borderColor: meta.color, borderWidth: 1, borderStyle: "solid", backgroundColor: `${meta.color}1A` }}
                      >
                        <meta.Icon className="w-3 h-3" />
                        {meta.label}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--rg-text)] leading-relaxed mt-2">{info.summary}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Remediation */}
        <div className="mt-10">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Recommendations</div>
          <h2 className="font-heading font-bold tracking-tight text-2xl sm:text-3xl text-[var(--rg-text-strong)] mb-6">Top items sorted by impact</h2>
          <div className="space-y-3">
            {D.remediationItems.map((item, i) => {
              const meta = PRIORITY_META[item.priority] || PRIORITY_META.LOW;
              return (
                <div key={`${item.priority}-${item.action}`} className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`demo-remediation-${i}`}>
                  <div className="flex items-center gap-2 px-2.5 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wide" style={{ color: meta.color, borderColor: meta.color, borderWidth: 1, borderStyle: "solid", backgroundColor: `${meta.color}1a` }}>
                    <meta.Icon className="w-3.5 h-3.5" />
                    {item.priority}
                  </div>
                  <div className="flex-1">
                    <div className="text-[var(--rg-text-strong)] font-semibold">{item.action}</div>
                    <div className="text-xs text-[var(--rg-muted)] font-mono mt-1">
                      {item.jurisdiction} · {item.statuteCitation}{" "}
                      {item.officialURL && (
                        <a href={item.officialURL} target="_blank" rel="noreferrer" className="text-[var(--rg-sage)] hover:underline inline-flex items-center gap-1 ml-1">
                          official source <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="text-[var(--rg-sage)] font-mono font-bold text-lg">+{item.potentialGain} pts</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Data freshness footer — both tabs */}
        <div className="mt-8 rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-3 text-xs text-[var(--rg-text)] font-mono" data-testid="demo-data-freshness">
          Regulatory data last verified: <span data-testid="demo-last-verified">{liveVerification?.last_verified_date || DATA_FRESHNESS.lastVerified}</span>
          <br />
          Next scheduled review: {liveVerification?.next_review_date || DATA_FRESHNESS.nextReview}
        </div>

        {/* Final CTA */}
        <div className="mt-8 rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-8 text-center" data-testid="demo-final-cta">
          <h3 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-2">This is just a sample.</h3>
          <p className="text-[var(--rg-text)] mb-5">Your store has its own risk profile.</p>
          <Link to="/register">
            <Button size="lg" className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold">
              Audit My Real Store → <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        <div className="mt-8">
          <Disclaimer />
        </div>
      </section>
    </Layout>
  );
}
